import { Check } from "lucide-react";

// Default state for the engine build-spec section (tuning requests only).
export const emptyBuild = {
  engine_block: "stock", // stock | built
  forged_pistons: false,
  forged_rods: false,
  crank: "stock", // stock | forged | stroker | destroked
  valves: "stock", // stock | +1mm | +2mm | custom
  valves_custom: "",
  cam_brand: "",
  cam_duration: "",
  cam_vvt: "vvt", // vvt | deleted
  valvesprings: "stock", // stock | aftermarket
  valvespring_pressure: "",
  injectors: "stock", // stock | aftermarket
  injector_brand: "",
  injector_cc: "",
  fuel_pump: "stock", // stock | aftermarket
  fuel_pump_model: "",
  forced_induction: "stock", // stock | aftermarket
  fi_type: "turbo", // turbo | supercharger
  fi_model: "",
  air_intake: "stock", // stock | aftermarket
  air_intake_model: "",
  air_intake_diameter: "",
  exhaust: "stock", // stock | aftermarket
  exhaust_brand: "",
  exhaust_size: "",
  headers: "stock", // stock | aftermarket
  headers_detail: "",
  downpipe: "stock", // stock | aftermarket
  downpipe_brand: "",
  downpipe_diameter: "",
  map_sensor: "stock", // stock | aftermarket
  map_brand: "",
  map_bar: "",
  map_pn: "",
};

const cap = (x) => (x ? x.charAt(0).toUpperCase() + x.slice(1) : x);
const t = (x) => (x || "").trim();

// Turns the raw spec into a flat, human-readable [{label, value}] build sheet.
export function summarizeBuildSpec(s) {
  const e = [];
  const row = (label, value) => e.push({ label, value });

  if (s.engine_block === "built") {
    row("Engine Block", "Built");
    row("Pistons", s.forged_pistons ? "Forged" : "Stock");
    row("Rods", s.forged_rods ? "Forged" : "Stock");
    row("Crank", cap(s.crank));
    row("Valves", s.valves === "custom" ? `Custom${t(s.valves_custom) ? ` (${t(s.valves_custom)})` : ""}` : ({ stock: "Stock", "+1mm": "Larger +1mm", "+2mm": "Larger +2mm" }[s.valves] || s.valves));
    if (t(s.cam_brand) || t(s.cam_duration)) {
      row("Cams", [t(s.cam_brand), t(s.cam_duration) && `${t(s.cam_duration)}° dur`, s.cam_vvt === "deleted" ? "VVT deleted" : "VVT"].filter(Boolean).join(" · "));
    }
    row("Valvesprings", s.valvesprings === "aftermarket" ? `Aftermarket${t(s.valvespring_pressure) ? ` @ ${t(s.valvespring_pressure)}` : ""}` : "Stock");
  } else {
    row("Engine Block", "Stock");
  }

  row("Injectors", s.injectors === "aftermarket"
    ? ([t(s.injector_brand), t(s.injector_cc) && `${t(s.injector_cc)}cc`].filter(Boolean).join(" · ") || "Aftermarket")
    : "Stock");
  row("Fuel Pump", s.fuel_pump === "aftermarket" ? `Aftermarket${t(s.fuel_pump_model) ? ` (${t(s.fuel_pump_model)})` : ""}` : "Stock");
  row("Forced Induction", s.forced_induction === "aftermarket" ? ([cap(s.fi_type), t(s.fi_model)].filter(Boolean).join(" · ") || "Aftermarket") : "Stock");
  row("Air Intake", s.air_intake === "aftermarket" ? ([t(s.air_intake_model), t(s.air_intake_diameter)].filter(Boolean).join(" · ") || "Aftermarket") : "Stock");
  row("Exhaust", s.exhaust === "aftermarket" ? ([t(s.exhaust_brand), t(s.exhaust_size)].filter(Boolean).join(" · ") || "Aftermarket") : "Stock");
  row("Headers / Manifold", s.headers === "aftermarket" ? (t(s.headers_detail) || "Aftermarket") : "Stock");
  row("Downpipe", s.downpipe === "aftermarket" ? ([t(s.downpipe_brand), t(s.downpipe_diameter)].filter(Boolean).join(" · ") || "Aftermarket") : "Stock");
  row("MAP Sensor", s.map_sensor === "aftermarket" ? ([t(s.map_brand), t(s.map_bar) && `${t(s.map_bar)} bar`, t(s.map_pn) && `PN ${t(s.map_pn)}`].filter(Boolean).join(" · ") || "Aftermarket") : "Stock");

  return e;
}

const fieldCls = "w-full bg-void border border-[#222] px-3 py-2.5 font-mono text-sm text-[#FAFAFA] placeholder:text-[#555] outline-none focus:border-neon rounded-none";
const selCls = `${fieldCls} appearance-none cursor-pointer uppercase tracking-wide`;

const Seg = ({ value, onChange, options, testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {options.map((o) => (
      <button
        type="button"
        key={o.value}
        data-testid={`${testid}-${o.value}`}
        onClick={() => onChange(o.value)}
        className={`px-4 py-2 text-xs uppercase tracking-[0.15em] border transition-colors ${value === o.value ? "border-neon text-neon bg-neon/5" : "border-[#222] text-[#9CA3AF] hover:border-[#444]"}`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const Lb = ({ label, children }) => (
  <label className="block">
    <span className="block text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF] mb-1.5">{label}</span>
    {children}
  </label>
);

const Grp = ({ title, right, children }) => (
  <div className="border-t border-[#1A1A1A] pt-4">
    <div className="flex items-center justify-between gap-3 mb-3">
      <div className="text-xs uppercase tracking-[0.2em] text-[#FAFAFA]">{title}</div>
      {right}
    </div>
    {children}
  </div>
);

const Chk = ({ checked, onChange, label, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={() => onChange(!checked)}
    className={`flex items-center gap-2.5 border px-3 py-2.5 text-left w-full transition-colors ${checked ? "border-neon bg-neon/5" : "border-[#222] hover:border-[#444]"}`}
  >
    <span className={`w-4 h-4 border flex items-center justify-center shrink-0 ${checked ? "border-neon bg-neon" : "border-[#555]"}`}>
      {checked && <Check size={12} className="text-void" />}
    </span>
    <span className="font-grotesk text-sm text-[#FAFAFA]">{label}</span>
  </button>
);

const stockAftermarket = [
  { value: "stock", label: "Stock" },
  { value: "aftermarket", label: "Aftermarket" },
];

export function BuildSpec({ value, onChange }) {
  const s = value;
  const set = (k, v) => onChange({ ...s, [k]: v });

  return (
    <div data-testid="build-spec" className="border border-[#222] bg-matte">
      <div className="px-5 py-4 border-b border-[#222] flex items-center justify-between">
        <span className="font-display uppercase text-xl tracking-tight">Engine <span className="text-neon">Build Spec</span></span>
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF]">Optional — leave stock if unsure</span>
      </div>

      <div className="p-5 space-y-5">
        {/* Engine block */}
        <div>
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="text-xs uppercase tracking-[0.2em] text-[#FAFAFA]">Engine Block</div>
            <Seg testid="engine-block-toggle" value={s.engine_block} onChange={(v) => set("engine_block", v)}
              options={[{ value: "stock", label: "Stock" }, { value: "built", label: "Built" }]} />
          </div>

          {s.engine_block === "built" && (
            <div className="space-y-4 border border-[#1A1A1A] p-4" data-testid="block-internals">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Chk testid="forged-pistons" checked={s.forged_pistons} onChange={(v) => set("forged_pistons", v)} label="Forged pistons" />
                <Chk testid="forged-rods" checked={s.forged_rods} onChange={(v) => set("forged_rods", v)} label="Forged rods" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Lb label="Crank">
                  <select data-testid="crank-select" className={selCls} value={s.crank} onChange={(e) => set("crank", e.target.value)}>
                    <option value="stock">Stock</option>
                    <option value="forged">Forged</option>
                    <option value="stroker">Stroker</option>
                    <option value="destroked">Destroked</option>
                  </select>
                </Lb>
                <Lb label="Valves">
                  <select data-testid="valves-select" className={selCls} value={s.valves} onChange={(e) => set("valves", e.target.value)}>
                    <option value="stock">Stock</option>
                    <option value="+1mm">Larger +1mm</option>
                    <option value="+2mm">Larger +2mm</option>
                    <option value="custom">Custom size</option>
                  </select>
                </Lb>
              </div>
              {s.valves === "custom" && (
                <Lb label="Custom valve size">
                  <input data-testid="valves-custom" className={fieldCls} value={s.valves_custom} onChange={(e) => set("valves_custom", e.target.value)} placeholder="e.g. intake +1.5mm / exhaust +1mm" />
                </Lb>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Lb label="Cam brand"><input data-testid="cam-brand" className={fieldCls} value={s.cam_brand} onChange={(e) => set("cam_brand", e.target.value)} placeholder="Kelford / GSC…" /></Lb>
                <Lb label="Cam duration"><input data-testid="cam-duration" className={fieldCls} value={s.cam_duration} onChange={(e) => set("cam_duration", e.target.value)} placeholder="272 / 264…" /></Lb>
                <Lb label="VVT">
                  <select data-testid="cam-vvt-select" className={selCls} value={s.cam_vvt} onChange={(e) => set("cam_vvt", e.target.value)}>
                    <option value="vvt">VVT retained</option>
                    <option value="deleted">VVT deleted</option>
                  </select>
                </Lb>
              </div>
              <div>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <span className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Valvesprings</span>
                  <Seg testid="valvesprings-toggle" value={s.valvesprings} onChange={(v) => set("valvesprings", v)} options={stockAftermarket} />
                </div>
                {s.valvesprings === "aftermarket" && (
                  <Lb label="Spring pressure"><input data-testid="valvespring-pressure" className={fieldCls} value={s.valvespring_pressure} onChange={(e) => set("valvespring_pressure", e.target.value)} placeholder="e.g. 90 lb seat / 220 lb open" /></Lb>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Fueling */}
        <Grp title="Fueling">
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between gap-3 mb-2">
                <span className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Fuel injectors</span>
                <Seg testid="injectors-toggle" value={s.injectors} onChange={(v) => set("injectors", v)} options={stockAftermarket} />
              </div>
              {s.injectors === "aftermarket" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Lb label="Injector brand"><input data-testid="injector-brand" className={fieldCls} value={s.injector_brand} onChange={(e) => set("injector_brand", e.target.value)} placeholder="Injector Dynamics / Bosch…" /></Lb>
                  <Lb label="Injector flow (cc)"><input data-testid="injector-cc" className={fieldCls} value={s.injector_cc} onChange={(e) => set("injector_cc", e.target.value)} placeholder="1050 / 2000…" /></Lb>
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between gap-3 mb-2">
                <span className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Fuel pump</span>
                <Seg testid="fuel-pump-toggle" value={s.fuel_pump} onChange={(v) => set("fuel_pump", v)} options={stockAftermarket} />
              </div>
              {s.fuel_pump === "aftermarket" && (
                <Lb label="Fuel pump model"><input data-testid="fuel-pump-model" className={fieldCls} value={s.fuel_pump_model} onChange={(e) => set("fuel_pump_model", e.target.value)} placeholder="Walbro 450 / DW400…" /></Lb>
              )}
            </div>
          </div>
        </Grp>

        {/* Forced induction */}
        <Grp title="Forced Induction" right={<Seg testid="fi-toggle" value={s.forced_induction} onChange={(v) => set("forced_induction", v)} options={stockAftermarket} />}>
          {s.forced_induction === "aftermarket" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Lb label="Type">
                <select data-testid="fi-type-select" className={selCls} value={s.fi_type} onChange={(e) => set("fi_type", e.target.value)}>
                  <option value="turbo">Turbo</option>
                  <option value="supercharger">Supercharger</option>
                </select>
              </Lb>
              <Lb label="Size / Model"><input data-testid="fi-model" className={fieldCls} value={s.fi_model} onChange={(e) => set("fi_model", e.target.value)} placeholder="Pulsar 6266 / Whipple 3.0L…" /></Lb>
            </div>
          )}
        </Grp>

        {/* Air intake */}
        <Grp title="Air Intake" right={<Seg testid="air-intake-toggle" value={s.air_intake} onChange={(v) => set("air_intake", v)} options={stockAftermarket} />}>
          {s.air_intake === "aftermarket" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Lb label="Model / Housing"><input data-testid="air-intake-model" className={fieldCls} value={s.air_intake_model} onChange={(e) => set("air_intake_model", e.target.value)} placeholder="AEM / custom…" /></Lb>
              <Lb label="Housing diameter"><input data-testid="air-intake-diameter" className={fieldCls} value={s.air_intake_diameter} onChange={(e) => set("air_intake_diameter", e.target.value)} placeholder="76mm / 3 in" /></Lb>
            </div>
          )}
        </Grp>

        {/* Exhaust */}
        <Grp title="Exhaust" right={<Seg testid="exhaust-toggle" value={s.exhaust} onChange={(v) => set("exhaust", v)} options={stockAftermarket} />}>
          {s.exhaust === "aftermarket" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Lb label="Brand / Model"><input data-testid="exhaust-brand" className={fieldCls} value={s.exhaust_brand} onChange={(e) => set("exhaust_brand", e.target.value)} placeholder="Invidia / custom…" /></Lb>
              <Lb label="Size"><input data-testid="exhaust-size" className={fieldCls} value={s.exhaust_size} onChange={(e) => set("exhaust_size", e.target.value)} placeholder="3 in / 4 in" /></Lb>
            </div>
          )}
        </Grp>

        {/* Headers / Manifold / Downpipe */}
        <Grp title="Headers / Manifold / Downpipe">
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between gap-3 mb-2">
                <span className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Headers / Manifold</span>
                <Seg testid="headers-toggle" value={s.headers} onChange={(v) => set("headers", v)} options={stockAftermarket} />
              </div>
              {s.headers === "aftermarket" && (
                <Lb label="Brand / Size"><input data-testid="headers-detail" className={fieldCls} value={s.headers_detail} onChange={(e) => set("headers_detail", e.target.value)} placeholder="e.g. equal-length / 1.75 in primaries" /></Lb>
              )}
            </div>
            <div className="border-t border-[#161616] pt-4">
              <div className="flex items-center justify-between gap-3 mb-2">
                <span className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Downpipe</span>
                <Seg testid="downpipe-toggle" value={s.downpipe} onChange={(v) => set("downpipe", v)} options={stockAftermarket} />
              </div>
              {s.downpipe === "aftermarket" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Lb label="Brand / Model"><input data-testid="downpipe-brand" className={fieldCls} value={s.downpipe_brand} onChange={(e) => set("downpipe_brand", e.target.value)} placeholder="e.g. Invidia catless / custom" /></Lb>
                  <Lb label="Diameter"><input data-testid="downpipe-diameter" className={fieldCls} value={s.downpipe_diameter} onChange={(e) => set("downpipe_diameter", e.target.value)} placeholder="e.g. 3 in / 76mm" /></Lb>
                </div>
              )}
            </div>
          </div>
        </Grp>

        {/* MAP sensor */}
        <Grp title="MAP Sensor" right={<Seg testid="map-toggle" value={s.map_sensor} onChange={(v) => set("map_sensor", v)} options={stockAftermarket} />}>
          {s.map_sensor === "aftermarket" && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Lb label="Brand"><input data-testid="map-brand" className={fieldCls} value={s.map_brand} onChange={(e) => set("map_brand", e.target.value)} placeholder="Bosch / AEM…" /></Lb>
              <Lb label="Bar rating"><input data-testid="map-bar" className={fieldCls} value={s.map_bar} onChange={(e) => set("map_bar", e.target.value)} placeholder="4 bar / 5 bar" /></Lb>
              <Lb label="Part number"><input data-testid="map-pn" className={fieldCls} value={s.map_pn} onChange={(e) => set("map_pn", e.target.value)} placeholder="0281002976…" /></Lb>
            </div>
          )}
        </Grp>
      </div>
    </div>
  );
}
